import { computed, type ComputedRef, ref } from 'vue'

import { ONBOARDING_FORM_STEPS, type OnboardingFormStepKey } from '../lib/onboardingFormSteps'
import type {
  MissingOnboardingArea,
  OnboardingFormStepStatus,
} from './useOnboardingDataCompleteness'

/**
 * Position in the manual enter-data form and the per-step "continue" guard.
 * The user can only move forward once every required field of the current
 * step is filled; going back is always allowed.
 */
export function useOnboardingFormSteps(options: {
  stepStatus: ComputedRef<Record<OnboardingFormStepKey, OnboardingFormStepStatus>>
  missingFieldsForStep: (step: OnboardingFormStepKey) => MissingOnboardingArea[]
}) {
  const { stepStatus, missingFieldsForStep } = options

  const currentFormStepIndex = ref(0)
  // Set when the user tries to leave the current step with missing fields, so
  // every inline error of that step shows, not only those of visited fields.
  const missingFieldsRevealed = ref(false)

  const currentFormStep = computed(() => ONBOARDING_FORM_STEPS[currentFormStepIndex.value].key)
  const isLastFormStep = computed(
    () => currentFormStepIndex.value === ONBOARDING_FORM_STEPS.length - 1
  )
  const currentStepMissingFields = computed(() => missingFieldsForStep(currentFormStep.value))

  const firstIncompleteStepIndex = (upTo: number = ONBOARDING_FORM_STEPS.length) =>
    ONBOARDING_FORM_STEPS.slice(0, upTo).findIndex(({ key }) => !stepStatus.value[key].complete)

  function setFormStep(index: number) {
    if (index === currentFormStepIndex.value) return
    currentFormStepIndex.value = index
    missingFieldsRevealed.value = false
  }

  /** Move to `index`, stopping at (and revealing) the first incomplete step on the way. */
  function goToFormStep(index: number): boolean {
    if (index <= currentFormStepIndex.value) {
      setFormStep(index)
      return true
    }
    const blocking = firstIncompleteStepIndex(index)
    if (blocking === -1) {
      setFormStep(index)
      return true
    }
    setFormStep(blocking)
    missingFieldsRevealed.value = true
    return false
  }

  const nextFormStep = () => goToFormStep(currentFormStepIndex.value + 1)

  function previousFormStep() {
    if (currentFormStepIndex.value > 0) setFormStep(currentFormStepIndex.value - 1)
  }

  /** Jump to the first step with missing data (used by the final continue guard). */
  function revealFirstIncompleteStep() {
    const index = firstIncompleteStepIndex()
    if (index !== -1) setFormStep(index)
    missingFieldsRevealed.value = true
  }

  /** On (re)entering the form, start where the user still has work to do — without errors. */
  function resumeAtFirstIncompleteStep() {
    const index = firstIncompleteStepIndex()
    if (index > currentFormStepIndex.value) setFormStep(index)
  }

  /** Show a step without the forward guard (e.g. to scroll to a field the assistant asks about). */
  function showFormStep(step: OnboardingFormStepKey) {
    setFormStep(ONBOARDING_FORM_STEPS.findIndex(({ key }) => key === step))
  }

  return {
    currentFormStepIndex,
    currentFormStep,
    isLastFormStep,
    currentStepMissingFields,
    missingFieldsRevealed,
    goToFormStep,
    nextFormStep,
    previousFormStep,
    revealFirstIncompleteStep,
    resumeAtFirstIncompleteStep,
    showFormStep,
  }
}
